import { useCallback, useState } from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import { toAppError, transformError, getErrorAction } from '@/lib/errors';
import { logComponentError } from '@/lib/logging/errorLogger';
import { addBreadcrumb } from '@/lib/logging/sentry';
import type { AppError } from '@/lib/errors/AppError';

/**
 * Hook for consistent error handling in components
 *
 * Provides:
 * - Error state management
 * - Error transformation and logging
 * - User-friendly error messages
 * - Error clearing
 *
 * @example
 * ```typescript
 * const { error, errorMessage, handleError, clearError } = useErrorHandler('LoginForm');
 *
 * try {
 *   await login(email, password);
 * } catch (err) {
 *   handleError(err);
 * }
 * ```
 */
export function useErrorHandler(componentName: string) {
  const { t } = useTranslation();
  const [error, setError] = useState<AppError | null>(null);

  /**
   * Handle an error: transform, log, and store
   */
  const handleError = useCallback(
    (err: unknown, context?: Record<string, unknown>) => {
      const appError = toAppError(err);

      // Add breadcrumb for error tracking (helps understand user journey)
      addBreadcrumb(`Error in ${componentName}: ${appError.message}`, 'error', {
        component: componentName,
        category: appError.category,
        severity: appError.severity,
        ...(context && { context: JSON.stringify(context) }),
      });

      // Log the error
      logComponentError(appError, componentName, context);

      // Store the error
      setError(appError);

      return appError;
    },
    [componentName]
  );

  /**
   * Clear the current error
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Get user-friendly error message
   */
  const errorMessage = error ? transformError(error, t) : null;

  /**
   * Get suggested action for the error
   */
  const errorAction = error ? getErrorAction(error, t) : null;

  /**
   * Check if there is an error
   */
  const hasError = error !== null;

  return {
    error,
    errorMessage,
    errorAction,
    hasError,
    handleError,
    clearError,
  };
}
